import { Select, Text } from "@ttc3k/trekker";
import { Wrench } from "iconoir-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { WhereaboutsModal } from "shared/components";
import { Flex } from "styled-system/jsx";

type StatusOption = { tagId: string; name: string };

type MarkStatusDialogProps = {
  count: number;
  /** Status these listings hold now. Null means they have no status tag. */
  fromTagId?: string | null;
  fromName?: string | null;
  statuses: StatusOption[];
  /** Renewing/Overdue: added beside the current status instead of replacing it. */
  attentionTagIds?: string[];
  defaultStatusId?: string | null;
  onApply: (statusTagId: string) => Promise<void>;
  onClose: () => void;
  membershipTranslationValues: Record<string, string>;
};

/** Picks which status to give a cohort of tiered listings. */
export const MarkStatusDialog = ({
  count,
  fromTagId,
  fromName,
  statuses,
  attentionTagIds = [],
  defaultStatusId,
  onApply,
  onClose,
  membershipTranslationValues,
}: MarkStatusDialogProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.MEMBERSHIP_TIERS.CHECKLIST.STATUS_TAGS",
  });
  const { t: tFix } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.MEMBERSHIP_TIERS.CHECKLIST.FIX",
  });
  const { t: tCore } = useTranslation("core");
  const choices = statuses.filter(s => s.tagId !== fromTagId);
  const [statusTagId, setStatusTagId] = useState(
    defaultStatusId && choices.some(s => s.tagId === defaultStatusId)
      ? defaultStatusId
      : "",
  );
  const [applying, setApplying] = useState(false);

  const items = useMemo(
    () => choices.map(s => ({ value: s.tagId, label: s.name })),
    [choices],
  );
  const collection = useMemo(
    () => Select.createCollection({ items }),
    [items],
  );
  const chosen = choices.find(s => s.tagId === statusTagId);

  return (
    <WhereaboutsModal
      isOpen
      onClose={onClose}
      title={t("MARK_MODAL_TITLE")}
      description={
        fromName
          ? t("MARK_MODAL_DESCRIPTION_FROM", {
              ...membershipTranslationValues,
              count,
              from: fromName,
            })
          : t("MARK_MODAL_DESCRIPTION", {
              ...membershipTranslationValues,
              count,
            })
      }
      HeaderIcon={Wrench}
      size="md"
      saveButtonCopy={tFix("CONFIRM")}
      cancelButtonCopy={tCore("CANCEL")}
      saveButtonProps={{
        visual: "accent.blue",
        disabled: applying || !chosen,
        isLoading: applying,
        onClick: async () => {
          if (!chosen) return;
          setApplying(true);
          try {
            await onApply(chosen.tagId);
          } finally {
            setApplying(false);
          }
        },
      }}
      cancelButtonProps={{ onClick: onClose }}
      isMaxZIndex
    >
      <Flex flexDir="column" gap="200">
        <Select.Root
          collection={collection}
          value={statusTagId ? [statusTagId] : []}
          onValueChange={d => setStatusTagId(d.value[0] ?? "")}
          label={t("MARK_MODAL_LABEL")}
          disabled={applying}
        >
          {items.map(option => (
            <Select.Item key={option.value} item={option} />
          ))}
        </Select.Root>
        {chosen && (
          <Text size="sm" color="text.mid">
            {fromName && attentionTagIds.includes(chosen.tagId)
              ? t("MARK_ADD_CONFIRM", {
                  count,
                  status: chosen.name,
                  from: fromName,
                })
              : fromName
                ? t("MARK_MOVE_CONFIRM", {
                    count,
                    status: chosen.name,
                    from: fromName,
                  })
                : t("MARK_CONFIRM", { count, status: chosen.name })}
          </Text>
        )}
        <Text size="sm" color="text.light">
          {tFix("QUIET_NOTE")}
        </Text>
      </Flex>
    </WhereaboutsModal>
  );
};
